// Data Fetching and Management

import { config } from './config.js';

class DataManager {
  constructor() {
    this.data = null;
    this.loading = false;
    this.lastFetch = 0;
  }

  async load(force = false) {
    // Check cache
    if (!force && this.data && Date.now() - this.lastFetch < config.CACHE_DURATION) {
      console.log('📦 Using cached data');
      return this.data;
    }

    if (this.loading) {
      // Wait for existing fetch
      return new Promise(resolve => {
        const check = setInterval(() => {
          if (!this.loading) {
            clearInterval(check);
            resolve(this.data);
          }
        }, 100);
      });
    }

    this.loading = true;
    const url = config.API_URL + '?t=' + Date.now();

    console.log('🔄 Fetching data from:', url);

    try {
      const response = await fetch(url);
      console.log('📡 Response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const rawText = await response.text();
      console.log('📝 Raw response (first 500 chars):', rawText.substring(0, 500));

      this.data = JSON.parse(rawText);
      console.log('✅ Data parsed successfully!');
      console.log('📊 Data structure:', {
        hasTraining: !!this.data.training,
        hasDiet: !!this.data.diet,
        weeks: this.data.training ? Object.keys(this.data.training) : [],
        sampleWeek: this.data.training?.W1 ? Object.keys(this.data.training.W1) : []
      });

      this.lastFetch = Date.now();
      return this.data;
    } catch (error) {
      console.error('❌ Error loading data:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack
      });
      throw error;
    } finally {
      this.loading = false;
    }
  }

  getData() {
    return this.data;
  }

  /**
   * Parse training data that has all exercises in one column
   * Splits exercises by day markers (LUNEDÌ, MERCOLEDÌ, VENERDÌ, SABATO)
   */
  parseTrainingData(exercises) {
    const days = {};
    let currentDay = null;
    let currentExercises = [];

    // Day markers to look for
    const dayMarkers = [
      { key: 'LUNEDÌ', label: 'LUNEDÌ (A)' },
      { key: 'MERCOLEDÌ', label: 'MERCOLEDÌ (B)' },
      { key: 'VENERDÌ', label: 'VENERDÌ (C)' },
      { key: 'SABATO', label: 'SABATO (D)' }
    ];

    for (const line of exercises) {
      const lineUpper = line.toUpperCase().trim();

      // Check if this line is a day marker
      const dayMarker = dayMarkers.find(d => lineUpper.includes(d.key));

      if (dayMarker) {
        // Save previous day's exercises
        if (currentDay && currentExercises.length > 0) {
          days[currentDay] = currentExercises.filter(ex =>
            ex &&
            ex.trim() !== '' &&
            ex.toLowerCase() !== 'esercizio'
          );
        }

        // Start new day
        currentDay = dayMarker.label;
        currentExercises = [];
      } else {
        // Add exercise to current day (skip headers and empty lines)
        if (currentDay && line.trim() !== '' &&
            !lineUpper.startsWith('📌') &&
            !lineUpper.includes('BASELINE') &&
            !lineUpper.includes('CONSOLIDAMENTO') &&
            !lineUpper.includes('AUMENTO PESO') &&
            line.toLowerCase() !== 'esercizio') {
          currentExercises.push(line.trim());
        }
      }
    }

    // Save last day's exercises
    if (currentDay && currentExercises.length > 0) {
      days[currentDay] = currentExercises.filter(ex =>
        ex &&
        ex.trim() !== '' &&
        ex.toLowerCase() !== 'esercizio'
      );
    }

    console.log('📋 Parsed training data:', {
      daysFound: Object.keys(days),
      exercisesPerDay: Object.entries(days).map(([day, exs]) => `${day}: ${exs.length} exercises`)
    });

    return days;
  }

  getTraining(week, day) {
    console.log(`🔍 Looking for training: week="${week}", day="${day}"`);

    if (!this.data?.training?.[week]) {
      console.warn(`⚠️ Week "${week}" not found. Available weeks:`,
        this.data?.training ? Object.keys(this.data.training) : 'No training data');
      return null;
    }

    const weekData = this.data.training[week];
    console.log(`✅ Week "${week}" found. Keys:`, Object.keys(weekData));

    // Get the first (and usually only) key in the week
    const firstKey = Object.keys(weekData)[0];
    const allExercises = weekData[firstKey];

    if (!Array.isArray(allExercises)) {
      console.warn(`⚠️ Expected array of exercises, got:`, typeof allExercises);
      return null;
    }

    console.log(`📝 Total lines in data:`, allExercises.length);

    // Parse the exercises to split by day
    const parsedDays = this.parseTrainingData(allExercises);

    // Find matching day
    for (const [dayKey, exercises] of Object.entries(parsedDays)) {
      if (dayKey.startsWith(day)) {
        console.log(`✅ Found matching day: "${dayKey}"`);
        console.log(`📋 Exercises:`, exercises);
        return exercises;
      }
    }

    console.warn(`⚠️ No day starting with "${day}" found. Available days:`, Object.keys(parsedDays));
    return null;
  }

  getDiet(day) {
    return this.data?.diet?.[day] || null;
  }
}

export const dataManager = new DataManager();
